/**
 * -----------------------------------------------------------------------------
 * Geozones Listing Page
 * -----------------------------------------------------------------------------
 * This page is used to list all the geozones of the organization.
 */

import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useDebouncedCallback } from "use-debounce";
import { useOrganizationGeozonesQuery } from "../../../api/network/adminApiServices";
import {
  TListingQueryParams,
  TModalsState,
  setListingQueryParams,
  setModalsData,
} from "../../../api/store/commonSlice";
import { TLegacyGeozone } from "../../../api/types/Geozone";
import { TSelectboxOption } from "../../../components/admin/formFields";
import HeaderView from "../../../components/admin/headerView";
import AdminTable from "../../../components/adminTable";
import ListingPageSubHeader from "../../../components/listingPageSubHeader";
import ListingTableHeader from "../../../components/listingTableHeader";
import {
  EditListingColumnsButton,
  ModalEditListingColumns,
  TListingColumn,
} from "../../../components/modalEditListingColumns";
import Pagination, {
  TPaginationSelected,
} from "../../../components/pagination";
import { TAdminTableRowData } from "../../../components/types";
import { APP_CONFIG } from "../../../constants/constants";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { useMapInit } from "../../../hooks/mapInit";
import { routeUrls } from "../../../navigation/routeUrls";
import { TMapState } from "../../../types/map";
import { SortingStatusWithReset } from "../../../utils/adminListings";
import {
  getGeozoneShapeCenter,
  getGeozoneShapeDescription,
  getGeozoneShapeTitle,
} from "../../../utils/geozone";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../../utils/localStorageItems";
import { useLoggedInUserData } from "../../../utils/user";
import { DISTANCE_UNIT_VALUES } from "../../admin-details/account/constants";
import { TAutosuggestOptionValue } from "../../admin-details/geozone/type";
import { ROLE_ACCESS_LEVELS } from "../../admin-details/role/constants";
import AdminsGeozonesCreateNew from "./createNewGeozone";

const all_columns = [
  "Zone ID",
  "Description",
  "Overlap Priority",
  "Zone Type",
  "Reverse Geocode",
  "Arrival Zone",
  "Departure Zone",
  "Shape Details",
  "Center",
];

const columnOrderingFields: Record<string, string> = {
  "Zone ID": "geozoneid",
  Description: "description",
  // "Overlap Priority": "", // intentionally excluded from sortability
  "Zone Type": "zonetype",
  "Reverse Geocode": "reversegeocode",
  "Arrival Zone": "arrivalzone",
  "Departure Zone": "departurezone",
};

export const geozoneDescriptionDisplayText = (
  description: string | null,
  defaultText: string
) => {
  let displayText = defaultText;
  try {
    if (description && description !== "null") {
      const descriptionObj: TAutosuggestOptionValue = JSON.parse(description);
      displayText = descriptionObj.labelText;
    }
    return displayText ?? description ?? defaultText;
  } catch (e) {
    return description ?? defaultText;
  }
};

const ScreenDashboardAdminGeozones = () => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", { keyPrefix: "admins.geozones" });
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { geoZones: orgGeozonesQueryParams } = listingQueryParams;
  const mapState = useSelector(
    (state: { commonReducer: { mapState: TMapState } }) =>
      state.commonReducer.mapState
  );
  const dispatch = useDispatch();
  const listingWrapperRef = React.useRef<HTMLDivElement>(null);

  const { getUserACLAccessLevel } = useACL();

  const orgDistanceUnit = useLoggedInUserData(
    "distanceUnits"
  ) as DISTANCE_UNIT_VALUES;

  // columns
  const { getLocalStorageItem } = useLocalStorage();
  const savedColumns = getLocalStorageItem(localStorageKeys.columns.Geozones);
  const [columns, setColumns] = React.useState<TListingColumn[]>(
    all_columns.map((colItem, _) => {
      return {
        name: colItem,
        show: !!savedColumns ? savedColumns.includes(colItem) : true,
      };
    })
  );

  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        geoZones: { ...orgGeozonesQueryParams, page: 1, search: value },
      })
    );
  }, 500);

  const handleOrderingChange = (ordering?: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        geoZones: {
          ...orgGeozonesQueryParams,
          ordering,
          page: 1,
        },
      })
    );
  };

  // due to page components being dependent on map,
  // after 5 seconds of component mount, check if map script is loaded, load if not
  const { initMap } = useMapInit();
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!mapState?.mapScriptLoaded) {
        initMap();
      }
    }, 1000 * 5);
    return () => clearTimeout(timer);
  }, [mapState?.mapScriptLoaded]);

  // fetch geozones data
  const {
    data: dataOrgGeozones,
    isFetching: isFetchingOrgGeozones,
    error,
  } = useOrganizationGeozonesQuery(
    {
      ...orgGeozonesQueryParams,
      // 'search_fields' intentionally not added here as default search fields are defined in the API service
    },
    {
      skip: !mapState?.mapScriptLoaded,
    }
  );
  const { count, next, previous, results } = dataOrgGeozones || {};

  // smooth scroll listing to the top on page change
  useEffect(() => {
    listingWrapperRef?.current?.scrollTo({ behavior: "smooth", top: 0 });
  }, [dataOrgGeozones]);

  // preparing table data
  const tableData: TAdminTableRowData[] = !!results
    ? (results || ([] as TLegacyGeozone[])).map(
        (item: TLegacyGeozone, index: number) => ({
          navLink: `${routeUrls.dashboardChildren.adminChildren.geozones}/${item.geozoneid}`,
          cellData: [
            item?.geozoneid ?? "-", // Geozone Id
            geozoneDescriptionDisplayText(item?.description, "-"), // Description
            item?.priority ?? "-", // Overlap Priority
            getGeozoneShapeTitle(item) ?? "-", // Zone Type
            item?.reversegeocode ? (
              <span className="text-field-success">{tMain("yes")}</span>
            ) : (
              <span className="text-field-error-dark">{tMain("no")}</span>
            ), // Reverse Geocode
            item?.arrivalzone ? (
              <span className="text-field-success">{tMain("yes")}</span>
            ) : (
              <span className="text-field-error-dark">{tMain("no")}</span>
            ), // Arrival Zone
            item?.departurezone ? (
              <span className="text-field-success">{tMain("yes")}</span>
            ) : (
              <span className="text-field-error-dark">{tMain("no")}</span>
            ), // Departure Zone
            getGeozoneShapeDescription(item, orgDistanceUnit) ?? "-", // Radius // TODO: determine shape details
            getGeozoneShapeCenter(item) || "-", // Center // TODO: determine center
          ].filter((_, index) => columns[index].show),
        })
      )
    : [];

  // rendering
  return (
    <>
      <HeaderView title={t("heading")} />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} pt-4`}>
        <ListingPageSubHeader
          heading={t("sub_heading")}
          {...(getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_GEOZONE) >=
            ROLE_ACCESS_LEVELS.ALL && {
            buttonText: t("add_new"),
            buttonCallback: () => {
              dispatch(
                setModalsData({ ...modalsState, showCreateGeozone: true })
              );
            },
          })}
        />
        <ListingTableHeader
          heading={
            <div className="flex items-center gap-4">
              {/* Heading */}
              <span>{t("listing_heading")}</span>

              {/* Sorted by label */}
              {!!orgGeozonesQueryParams?.ordering && (
                <SortingStatusWithReset
                  ordering={orgGeozonesQueryParams.ordering}
                  columnOrderingFields={columnOrderingFields}
                  onResetOrdering={() => handleOrderingChange(undefined)}
                />
              )}
            </div>
          }
          searchBoxPlaceholder={t("search_placeholder")}
          searchBoxValue={orgGeozonesQueryParams.search}
          searchBoxOnChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            debouncedSetSearchKeyword(e.target.value)
          }
          searchBoxOnClear={() => debouncedSetSearchKeyword("")}
        />
        <ModalEditListingColumns
          columns={columns}
          setColumns={setColumns}
          lsKey={localStorageKeys.columns.Geozones}
        />
        <div className="py-4 mt-4 relative">
          <EditListingColumnsButton disabled={isFetchingOrgGeozones} />
          <AdminTable
            viewportHeightOffset={"400"}
            columns={columns
              .filter((item) => item.show)
              .map((item) => item.name)}
            data={tableData}
            isLoading={isFetchingOrgGeozones || !mapState?.mapScriptLoaded}
            listingQueryParams={orgGeozonesQueryParams}
            listingWrapperRef={listingWrapperRef}
            onOrderingChange={handleOrderingChange}
            ordering={orgGeozonesQueryParams.ordering}
            sortableColumns={columnOrderingFields}
          />
          {!isFetchingOrgGeozones && (
            <Pagination
              pageSize={orgGeozonesQueryParams.page_size}
              handlePageSizeChange={(e: TSelectboxOption | null) => {
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    geoZones: {
                      ...orgGeozonesQueryParams,
                      page_size: parseInt(`${e?.value}`),
                    },
                  })
                );
              }}
              totalPages={
                count ? Math.ceil(count / orgGeozonesQueryParams.page_size) : 1
              }
              forcePage={orgGeozonesQueryParams.page - 1}
              handlePageClick={(data: TPaginationSelected) => {
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    geoZones: {
                      ...orgGeozonesQueryParams,
                      page: data?.selected + 1,
                    },
                  })
                );
              }}
              paginCurrentPage={orgGeozonesQueryParams.page}
              paginItemsOnCurrentPage={results?.length || 0}
              paginTotalItemsCount={count}
            />
          )}
        </div>
      </div>
      <AdminsGeozonesCreateNew />
    </>
  );
};

export default ScreenDashboardAdminGeozones;
